import axios, { AxiosInstance, AxiosRequestConfig } from 'axios';
import { setContentMediaBase } from '@easytools/tool-content';
import { auth } from '../auth/firebaseConfig';

const normalizeApiBaseUrl = (value: string | undefined, fallback: string): string => {
    const raw = (value || fallback).trim().replace(/\/+$/, '');
    if (!raw || raw.startsWith('/') || /^https?:\/\//i.test(raw)) {
        return raw;
    }
    return `https://${raw}`;
};

const BASE_URL = normalizeApiBaseUrl(import.meta.env.VITE_API_BASE_URL, 'https://localhost:5001');

// Le immagini della libreria dei contenuti (media:<id>) si leggono dall'API.
setContentMediaBase(BASE_URL);

/**
 * ApiService dell'app admin. Volutamente piu' essenziale di quello dell'app
 * tools: niente diagnostic-reporter (la pipeline di errori si gestisce qui
 * con un toast pulito), niente speciali handler per endpoint diversi.
 *
 * Auth: token Firebase Bearer iniettato dall'interceptor. Il backend
 * verifica il ruolo "Admin" via policy "AdminOnly" sui controller admin.
 */
class AdminApiService {
    private axiosInstance: AxiosInstance;

    constructor() {
        this.axiosInstance = axios.create({
            baseURL: BASE_URL,
            timeout: 30_000,
        });

        this.axiosInstance.interceptors.request.use(async config => {
            const user = auth.currentUser;
            if (user) {
                const token = await user.getIdToken();
                config.headers = config.headers ?? {};
                (config.headers as Record<string, string>).Authorization = `Bearer ${token}`;
            }
            return config;
        });
    }

    // ────────── BaseURL helpers (per costruire URL di immagini) ──────────
    /** URL pubblica dell'immagine tool, identica a quella usata dal portale principale. */
    toolImageUrl(toolCode: string, role: string): string {
        return `${BASE_URL}/api/Tools/image/${encodeURIComponent(toolCode)}/${encodeURIComponent(role)}`;
    }
    getApiBaseUrl(): string {
        const resolved = new URL(BASE_URL || '/', window.location.origin);
        return resolved.toString().replace(/\/+$/, '');
    }


    // ────────── Admin Tools ──────────
    async adminGetTools<T = unknown>(): Promise<T[]> {
        return this.get<T[]>('/api/admin/tools');
    }
    async adminGetTool<T = unknown>(id: number): Promise<T> {
        return this.get<T>(`/api/admin/tools/${id}`);
    }
    async adminUpdateTool<T = unknown>(id: number, dto: unknown): Promise<T> {
        return this.put<T>(`/api/admin/tools/${id}`, dto);
    }
    // Contenuti del tool (pagina pubblica, scheda, guida): bozza, pubblicazione, storico.
    async adminGetToolContents<T = unknown>(toolId: number): Promise<T[]> {
        return this.get<T[]>(`/api/admin/tools/${toolId}/contents`);
    }
    async adminGetToolContent<T = unknown>(toolId: number, kind: string, language: string): Promise<T> {
        return this.get<T>(`/api/admin/tools/${toolId}/contents/${kind}/${language}`);
    }
    async adminGetToolContentHistory<T = unknown>(toolId: number, kind: string, language: string): Promise<T[]> {
        return this.get<T[]>(`/api/admin/tools/${toolId}/contents/${kind}/${language}/history`);
    }
    async adminGetToolContentVersion<T = unknown>(toolId: number, kind: string, language: string, version: number): Promise<T> {
        return this.get<T>(`/api/admin/tools/${toolId}/contents/${kind}/${language}/versions/${version}`);
    }
    async adminPrepareToolContentGeneration<T = unknown>(toolId: number, kind: string, language: string,
        request: { mode: string; instructions?: string; blockIndex?: number; blocks?: string }): Promise<T> {
        return this.post<T>(`/api/admin/tools/${toolId}/contents/${kind}/${language}/generation/prepare`, request);
    }
    async adminCompleteToolContentGeneration<T = unknown>(toolId: number, kind: string, language: string,
        result: { mode: string; output: string; agent?: string }): Promise<T> {
        return this.post<T>(`/api/admin/tools/${toolId}/contents/${kind}/${language}/generation/complete`, result);
    }
    async adminSaveToolContentDraft<T = unknown>(toolId: number, kind: string, language: string,
        draft: { content: string; origin?: string; notes?: string; sourceVersion?: number | null }): Promise<T> {
        return this.put<T>(`/api/admin/tools/${toolId}/contents/${kind}/${language}/draft`, draft);
    }
    async adminDiscardToolContentDraft(toolId: number, kind: string, language: string) {
        return this.delete(`/api/admin/tools/${toolId}/contents/${kind}/${language}/draft`);
    }
    async adminPublishToolContent<T = unknown>(toolId: number, kind: string, language: string): Promise<T> {
        return this.post<T>(`/api/admin/tools/${toolId}/contents/${kind}/${language}/publish`);
    }
    async adminRestoreToolContent<T = unknown>(toolId: number, kind: string, language: string, version: number, replaceDraft = false): Promise<T> {
        return this.post<T>(`/api/admin/tools/${toolId}/contents/${kind}/${language}/restore/${version}`, undefined,
            { params: { replaceDraft } });
    }
    async adminGetToolOverview<T = unknown>(id: number): Promise<T> {
        return this.get<T>(`/api/admin/tools/${id}/overview`);
    }
    async adminBulkUpdateTools<T = unknown>(dto: { toolIds: number[]; status?: string; visibility?: string; toolType?: string; catalogScope?: string }): Promise<T[]> {
        return this.post<T[]>('/api/admin/tools/bulk-update', dto);
    }
    async adminGetToolUsers<T = unknown>(toolId: number): Promise<T[]> {
        return this.get<T[]>(`/api/admin/tools/${toolId}/users`);
    }
    async adminSetUserToolAccess(toolId: number, userId: number, isEnabled: boolean) {
        return this.put(`/api/admin/tools/${toolId}/users/${userId}`, { isEnabled });
    }
    async adminGetToolGuides<T = unknown>(toolId: number): Promise<T[]> {
        return this.get<T[]>(`/api/admin/tools/${toolId}/guides`);
    }
    async adminGetToolImages<T = unknown>(toolId: number): Promise<T[]> {
        return this.get<T[]>(`/api/admin/tools/${toolId}/images`);
    }
    async adminGetToolReports<T = unknown>(toolId: number): Promise<T[]> {
        return this.get<T[]>(`/api/admin/tools/${toolId}/reports`);
    }
    async adminUpdateReportStatus(toolId: number, reportId: number, status: string) {
        return this.put(`/api/admin/tools/${toolId}/reports/${reportId}`, { status });
    }
    async adminDeleteReport(toolId: number, reportId: number) {
        return this.delete(`/api/admin/tools/${toolId}/reports/${reportId}`);
    }
    async adminGetToolDiagnostics<T = unknown>(toolId: number): Promise<T[]> {
        return this.get<T[]>(`/api/admin/tools/${toolId}/diagnostics`);
    }
    async adminClearDiagnostics(toolId: number) {
        return this.delete(`/api/admin/tools/${toolId}/diagnostics`);
    }
    async adminGetUsers<T = unknown>(): Promise<T[]> {
        return this.get<T[]>('/api/admin/users');
    }
    async adminSetUserRole(userId: number, role: 'User' | 'Admin') {
        return this.put(`/api/admin/users/${userId}/role`, { role });
    }
    async adminSetUserApproval<T = unknown>(
        userId: number,
        approvalStatus: 'Pending' | 'Approved' | 'Rejected' | 'Suspended',
        toolIds?: number[],
        rejectedReason?: string
    ): Promise<T> {
        return this.put<T>(`/api/admin/users/${userId}/approval`, { approvalStatus, toolIds, rejectedReason });
    }
    async adminGetUserTools<T = unknown>(userId: number): Promise<T[]> {
        return this.get<T[]>(`/api/admin/users/${userId}/tools`);
    }
    async adminSetUserTools(userId: number, toolIds: number[]) {
        return this.put(`/api/admin/users/${userId}/tools`, { toolIds });
    }
    async adminGetUserDetail<T = unknown>(userId: number): Promise<T> {
        return this.get<T>(`/api/admin/users/${userId}`);
    }
    async adminBulkUserApproval<T = unknown>(body: {
        userIds: number[];
        approvalStatus: 'Pending' | 'Approved' | 'Rejected' | 'Suspended';
        rejectedReason?: string;
        addToolIds?: number[];
    }): Promise<T> {
        return this.post<T>('/api/admin/users/bulk-approval', body);
    }

    // ────────── Comunicazioni (bacheca del portale) ──────────
    async adminGetMessages<T = unknown>(): Promise<T[]> {
        return this.get<T[]>('/api/admin/messages');
    }
    async adminGetMessage<T = unknown>(id: number): Promise<T> {
        return this.get<T>(`/api/admin/messages/${id}`);
    }
    async adminCreateMessage<T = unknown>(dto: unknown): Promise<T> {
        return this.post<T>('/api/admin/messages', dto);
    }
    async adminUpdateMessage<T = unknown>(id: number, dto: unknown): Promise<T> {
        return this.put<T>(`/api/admin/messages/${id}`, dto);
    }
    async adminSetMessageActive<T = unknown>(id: number, active: boolean): Promise<T> {
        return this.post<T>(`/api/admin/messages/${id}/${active ? 'publish' : 'archive'}`);
    }
    async adminDeleteMessage(id: number) {
        return this.delete(`/api/admin/messages/${id}`);
    }
    async adminGetReportsCrossTool<T = unknown>(opts?: { status?: string; type?: string }): Promise<T[]> {
        const params = new URLSearchParams();
        if (opts?.status) params.set('status', opts.status);
        if (opts?.type) params.set('type', opts.type);
        const qs = params.toString();
        return this.get<T[]>(`/api/admin/reports${qs ? `?${qs}` : ''}`);
    }
    async adminGetAuditLog<T = unknown>(opts?: {
        userId?: number; minStatus?: number; pathContains?: string; sinceHours?: number; limit?: number;
    }): Promise<T[]> {
        const p = new URLSearchParams();
        if (opts?.userId) p.set('userId', String(opts.userId));
        if (opts?.minStatus) p.set('minStatus', String(opts.minStatus));
        if (opts?.pathContains) p.set('pathContains', opts.pathContains);
        if (opts?.sinceHours) p.set('sinceHours', String(opts.sinceHours));
        if (opts?.limit) p.set('limit', String(opts.limit));
        const qs = p.toString();
        return this.get<T[]>(`/api/admin/audit${qs ? `?${qs}` : ''}`);
    }

    // ────────── Diagnostica globale cross-tool ──────────
    async adminGetGlobalDiagnostics<T = unknown>(opts?: {
        level?: string; source?: string; toolCode?: string; sinceHours?: number; limit?: number;
    }): Promise<T> {
        const p = new URLSearchParams();
        if (opts?.level) p.set('level', opts.level);
        if (opts?.source) p.set('source', opts.source);
        if (opts?.toolCode) p.set('toolCode', opts.toolCode);
        if (opts?.sinceHours) p.set('sinceHours', String(opts.sinceHours));
        if (opts?.limit) p.set('limit', String(opts.limit));
        const qs = p.toString();
        return this.get<T>(`/api/admin/diagnostics${qs ? `?${qs}` : ''}`);
    }
    async adminGetDiagnosticEvent<T = unknown>(id: number): Promise<T> {
        return this.get<T>(`/api/admin/diagnostics/${id}`);
    }

    // ────────── Issue Engine diagnostico ──────────
    async adminGetDiagnosticIssues<T = unknown>(opts?: {
        status?: string; severity?: string; source?: string; toolCode?: string;
        search?: string; sinceHours?: number; limit?: number;
    }): Promise<T[]> {
        const p = new URLSearchParams();
        if (opts?.status) p.set('status', opts.status);
        if (opts?.severity) p.set('severity', opts.severity);
        if (opts?.source) p.set('source', opts.source);
        if (opts?.toolCode) p.set('toolCode', opts.toolCode);
        if (opts?.search) p.set('search', opts.search);
        if (opts?.sinceHours) p.set('sinceHours', String(opts.sinceHours));
        if (opts?.limit) p.set('limit', String(opts.limit));
        const qs = p.toString();
        return this.get<T[]>(`/api/admin/issues${qs ? `?${qs}` : ''}`);
    }
    async adminGetDiagnosticIssue<T = unknown>(id: number): Promise<T> {
        return this.get<T>(`/api/admin/issues/${id}`);
    }
    async adminUpdateDiagnosticIssueStatus(id: number, status: string, note?: string) {
        return this.put(`/api/admin/issues/${id}/status`, { status, note });
    }
    async adminUpdateDiagnosticIssueOwner(id: number, userId?: number | null) {
        return this.put(`/api/admin/issues/${id}/owner`, { userId: userId ?? null });
    }
    async adminGetDiagnosticIssueAnalyses<T = unknown>(issueId: number): Promise<T[]> {
        return this.get<T[]>(`/api/admin/issues/${issueId}/analyses`);
    }
    async adminGenerateDiagnosticIssueAnalysis<T = unknown>(issueId: number, provider = 'anthropic'): Promise<T> {
        return this.post<T>(`/api/admin/issues/${issueId}/analyses/api`, { provider }, { timeout: 120_000 });
    }
    async adminPrepareDiagnosticIssueCompanion<T = unknown>(issueId: number, agent: string): Promise<T> {
        return this.post<T>(`/api/admin/issues/${issueId}/analyses/companion/prepare`, { agent });
    }
    async adminCompleteDiagnosticIssueCompanion<T = unknown>(
        issueId: number, analysisId: number, output?: string, error?: string,
    ): Promise<T> {
        return this.post<T>(`/api/admin/issues/${issueId}/analyses/${analysisId}/companion/complete`,
            { output, error }, { timeout: 60_000 });
    }
    async adminReviewDiagnosticIssueAnalysis<T = unknown>(
        issueId: number, analysisId: number, decision: 'Approved' | 'Rejected', note?: string,
    ): Promise<T> {
        return this.put<T>(`/api/admin/issues/${issueId}/analyses/${analysisId}/review`, { decision, note });
    }
    async getCompanionInfo<T = unknown>(): Promise<T> {
        return this.get<T>('/api/companion/info');
    }
    async downloadDesktopAgent(): Promise<Blob> {
        const response = await this.axiosInstance.get<Blob>('/api/companion/download', {
            responseType: 'blob', timeout: 120_000,
        });
        return response.data;
    }
    async adminStartDesktopAgentPairing<T = unknown>(): Promise<T> {
        return this.post<T>('/api/admin/desktop-agent/pairing/start');
    }
    async adminGetDesktopAgentDevices<T = unknown>(): Promise<T[]> {
        return this.get<T[]>('/api/admin/desktop-agent/devices');
    }
    async adminRevokeDesktopAgent(id: string): Promise<void> {
        await this.delete(`/api/admin/desktop-agent/devices/${encodeURIComponent(id)}`);
    }
    async adminGetDiagnosticIssuePatchDrafts<T = unknown>(issueId: number): Promise<T[]> {
        return this.get<T[]>(`/api/admin/issues/${issueId}/patch-drafts`);
    }
    async adminPrepareDiagnosticIssuePatchDraft<T = unknown>(
        issueId: number, analysisId: number, agent: string,
    ): Promise<T> {
        return this.post<T>(`/api/admin/issues/${issueId}/patch-drafts/companion/prepare`, { analysisId, agent });
    }
    async adminCompleteDiagnosticIssuePatchDraft<T = unknown>(
        issueId: number, draftId: number, output?: string, error?: string,
    ): Promise<T> {
        return this.post<T>(`/api/admin/issues/${issueId}/patch-drafts/${draftId}/companion/complete`,
            { output, error }, { timeout: 60_000 });
    }
    async adminReviewDiagnosticIssuePatchDraft<T = unknown>(
        issueId: number, draftId: number, decision: 'Approved' | 'Rejected', note?: string,
    ): Promise<T> {
        return this.put<T>(`/api/admin/issues/${issueId}/patch-drafts/${draftId}/review`, { decision, note });
    }



    // ────────── MCP Explorer (stessi tool del server MCP standalone) ──────────
    async mcpListTools<T = unknown>(): Promise<T[]> {
        return this.get<T[]>('/api/admin/mcp/tools');
    }
    async mcpCallTool<T = unknown>(toolName: string, input: unknown): Promise<T> {
        return this.post<T>(`/api/admin/mcp/call/${encodeURIComponent(toolName)}`, { input });
    }

    async adminUploadToolImage(toolId: number, role: string, blob: Blob, w: number, h: number) {
        const form = new FormData();
        // Il content-type della parte deriva da blob.type (image/png dal cropper,
        // image/svg+xml per il passthrough SVG): l'estensione del filename lo segue.
        const ext = blob.type === 'image/svg+xml' ? 'svg' : 'png';
        form.append('file', blob, `${role}.${ext}`);
        form.append('width', String(w));
        form.append('height', String(h));
        const res = await this.axiosInstance.post(`/api/admin/tools/${toolId}/images/${role}`, form);
        return res.data;
    }
    async adminDeleteToolImage(toolId: number, role: string) {
        return this.delete(`/api/admin/tools/${toolId}/images/${role}`);
    }
    // Libreria immagini dei contenuti: i blocchi le citano come media:<id>.
    async adminListContentImages<T = unknown>(params: { toolId?: number; q?: string } = {}): Promise<T[]> {
        const query = new URLSearchParams();
        if (params.toolId) query.set('toolId', String(params.toolId));
        if (params.q?.trim()) query.set('q', params.q.trim());
        return this.get<T[]>(`/api/admin/content-images${query.size ? `?${query}` : ''}`);
    }
    async adminUploadContentImage<T = unknown>(file: File, toolId?: number, alt?: string): Promise<T> {
        const form = new FormData();
        form.append('file', file);
        if (toolId) form.append('toolId', String(toolId));
        if (alt?.trim()) form.append('alt', alt.trim());
        const res = await this.axiosInstance.post<T>('/api/admin/content-images', form, { timeout: 120_000 });
        return res.data;
    }
    async adminUpdateContentImage<T = unknown>(id: number, dto: { alt?: string; toolId?: number }): Promise<T> {
        const res = await this.axiosInstance.patch<T>(`/api/admin/content-images/${id}`, dto);
        return res.data;
    }
    async adminDeleteContentImage(id: number, force = false) {
        return this.delete(`/api/admin/content-images/${id}${force ? '?force=true' : ''}`);
    }

    async adminUploadToolGuide(toolId: number, language: string, title: string | undefined, file: File) {
        const form = new FormData();
        form.append('file', file);
        form.append('language', language);
        if (title) form.append('title', title);
        const res = await this.axiosInstance.post(`/api/admin/tools/${toolId}/guides`, form);
        return res.data;
    }
    async adminDeleteToolGuide(toolId: number, guideId: number) {
        return this.delete(`/api/admin/tools/${toolId}/guides/${guideId}`);
    }

    /** Diagnose chat stream (SSE). Stesso pattern dell'app tools. */
    async diagnoseChatStream(
        body: { toolCode: string; messages: { role: string; content: string }[] },
        signal: AbortSignal,
    ): Promise<ReadableStream<Uint8Array>> {
        const headers: Record<string, string> = { 'Content-Type': 'application/json' };
        try {
            const u = auth.currentUser;
            if (u) headers['Authorization'] = `Bearer ${await u.getIdToken()}`;
        } catch { /* il BE rispondera' 401 */ }
        const res = await fetch(`${BASE_URL}/api/admin/diagnose/chat`, {
            method: 'POST', headers, body: JSON.stringify(body), signal,
        });
        if (!res.ok || !res.body) {
            const txt = await res.text().catch(() => '');
            throw new Error(`Errore ${res.status}: ${txt || res.statusText}`);
        }
        return res.body;
    }

    async get<T>(url: string, config?: AxiosRequestConfig): Promise<T> {
        const r = await this.axiosInstance.get<T>(url, config);
        return r.data;
    }
    async post<T>(url: string, data?: unknown, config?: AxiosRequestConfig): Promise<T> {
        const r = await this.axiosInstance.post<T>(url, data, config);
        return r.data;
    }
    async put<T>(url: string, data?: unknown, config?: AxiosRequestConfig): Promise<T> {
        const r = await this.axiosInstance.put<T>(url, data, config);
        return r.data;
    }
    async patch<T>(url: string, data?: unknown, config?: AxiosRequestConfig): Promise<T> {
        const r = await this.axiosInstance.patch<T>(url, data, config);
        return r.data;
    }
    async delete<T>(url: string, config?: AxiosRequestConfig): Promise<T> {
        const r = await this.axiosInstance.delete<T>(url, config);
        return r.data;
    }
}

const apiService = new AdminApiService();
export default apiService;
